// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import type {
  ClosedReferralsTrackerItem,
  ClosedReferralsFilters,
} from "@modules/reporting/reducer/referral-tracker/types";
import type { PaginationExtendedType } from "@types";
import { formatDateTime } from "@transformers/date";
import actionCreators from "@modules/reporting/actionCreator";
import {
  selectClosedReferrals,
  selectClosedReferralsPagination,
  selectClosedReferralsStatuses,
} from "@modules/reporting/selectors/referral-tracker";
import { Col, GSGrid, Pagination, Row } from "@components";
import ClosedReferralsFilter from "./filter";
import "../styles.scss";
import {
  SUPPLIER_REFERRAL_REPORT_TYPE,
  SUPPLIER_REFERRAL_STATUSES,
} from "@modules/reporting/constants/reports";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  closedReferrals: ClosedReferralsTrackerItem[],
  pagination: PaginationExtendedType,
  closedReferralsStatuses: string[],
}>;

type PropsFromDispatch = $Exact<{
  getClosedReferrals: typeof actionCreators.GetSupplierClosedReferrals,
  getSupplierReferralDetails: typeof actionCreators.GetSupplierReferralDetails,
  getClosedReferralsStatuses: typeof actionCreators.GetSupplierClosedReferralsStatuses,
  export: typeof actionCreators.GetSupplierClosedReferralsExport,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;
type StateProp = $Exact<{
  filters: ClosedReferralsFilters,
}>;

export function ClosedReferralsSection(props: Props) {
  const [filters, setFilters] = useState({ status: SUPPLIER_REFERRAL_STATUSES.ALL });
  const columnsConfig = [
    {
      name: props.t("REFERRAL_ID"),
      width: 90,
      key: "referralId",
    },
    {
      name: props.t("EMPLOYEE_REF"),
      width: 150,
      align: "left",
      key: "employeeRef",
      tooltipField: "employeeRef",
    },
    {
      name: props.t("FIRST_NAME"),
      width: 150,
      align: "left",
      key: "firstName",
    },
    {
      name: props.t("LAST_NAME"),
      width: 150,
      align: "left",
      key: "lastName",
    },
    {
      name: props.t("DEPARTMENT"),
      width: 212,
      align: "left",
      key: "departmentNames",
      renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.departmentNames),
    },
    {
      name: props.t("REFERRAL_TYPE"),
      width: 150,
      key: "referral_type",
      renderCell: ({ row }: *) => {
        return row.referralType ? props.t(row.referralType) : props.t("N/A");
      },
    },
    {
      name: props.t("DATE_REFERRED"),
      key: "date_referred",
      width: 150,
      renderCell: ({ row }: *) => {
        return row.dateReferred ? formatDateTime(row.dateReferred) : "";
      },
    },
    {
      name: props.t("REASON_FOR_REFERRAL"),
      width: 212,
      key: "reason",
      tooltipField: "reason",
      align: "left",
      renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.reason),
    },
    {
      name: props.t("STATUS"),
      width: 200,
      key: "status",
      renderCell: ({ row }: *) => {
        let className = "text-positive";
        let text = props.t(row.status.name);

        if (row.status.type === "COMPLETED") {
          className = "";
          text = `${props.t(row.status.type)} - ${props.t(row.status.name)}`;
        } else if (row.status.type === "CANCELLED") {
          className = "text-negative";
          text = `${props.t(row.status.type)} - ${props.t(row.status.name)}`;
        }
        return (
          <div title="text" className={`${className} font-weight-bold text-ellipsis`}>
            {text}
          </div>
        );
      },
    },
    {
      name: props.t("DATE_CLOSED"),
      key: "date_closed",
      width: 150,
      renderCell: ({ row }: *) => {
        return row.dateClosed ? formatDateTime(row.dateClosed) : "";
      },
    },
    {
      name: props.t("DETAILS"),
      key: "details",
      width: 80,
      align: "center",
      frozen: "right",
      resizable: false,
      renderCell: ({ row }: *) => (
        <span onClick={handleDetailsClick(row)} className="material-icons pointer">
          search
        </span>
      ),
    },
  ];

  useEffect(() => {
    props.getClosedReferrals({
      clientId: props.clientId,
      page: 1,
      filers: filters,
    });
    props.getClosedReferralsStatuses({ clientId: props.clientId });
  }, []);

  function handlePaginationChange(page: number) {
    props.getClosedReferrals({
      clientId: props.clientId,
      page,
      filters: filters,
    });
  }

  function handleDetailsClick(row: *) {
    return function() {
      props.getSupplierReferralDetails({
        supplierReferralId: row.referralId,
        clientId: props.clientId,
        referralType: SUPPLIER_REFERRAL_REPORT_TYPE.CLOSED_REFERRALS,
      });
    };
  }

  function filterChange(filters: ClosedReferralsFilters) {
    setFilters(filters);
    props.getClosedReferrals({ clientId: props.clientId, page: 1, filters });
  }

  function filterReset() {
    handlePaginationChange(1);
  }

  function onExport(filters: ClosedReferralsFilters) {
    props.export({ clientId: props.clientId, filters });
  }

  const { closedReferrals, pagination } = props;

  return (
    <React.Fragment>
      <div className="w-100 d-flex closed-referrals-filter">
        <ClosedReferralsFilter
          onFilterChange={filterChange}
          onReset={filterReset}
          onExport={onExport}
          statuses={props.closedReferralsStatuses}
        />
      </div>

      <div className="w-100 d-flex flex-column">
        <GSGrid columns={columnsConfig} rows={closedReferrals} />

        <Pagination
          className="d-flex flex-fill justify-content-center"
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onChange={handlePaginationChange}
        />
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    closedReferrals: selectClosedReferrals(state),
    pagination: selectClosedReferralsPagination(state),
    closedReferralsStatuses: selectClosedReferralsStatuses(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getClosedReferrals: actionCreators.GetSupplierClosedReferrals,
  getSupplierReferralDetails: actionCreators.GetSupplierReferralDetails,
  getClosedReferralsStatuses: actionCreators.GetSupplierClosedReferralsStatuses,
  export: actionCreators.GetSupplierClosedReferralsExport,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ClosedReferralsSection));
